import { graphql } from "@/lib/generated"

export const ApplicationsQuery = graphql(`
  query Applications($search: String, $limit: Int, $offset: Int, $after: String, $first: Int) {
    applications(search: $search, limit: $limit, offset: $offset, after: $after, first: $first) {
      nodes {
        id
        name
        uid
        createdAt
        endpoints {
          id
          status
        }
      }
      pageInfo {
        total
        limit
        offset
        endCursor
        hasNextPage
      }
    }
  }
`)

export const ApplicationQuery = graphql(`
  query Application($id: UUID!) {
    application(id: $id) {
      id
      name
      uid
      createdAt
      endpoints {
        id
        applicationId
        url
        status
        rateLimitRps
        timeoutMs
        healthScore
        disabledReason
        createdAt
      }
    }
  }
`)

export const EndpointsQuery = graphql(`
  query Endpoints($applicationId: UUID, $status: EndpointStatus, $search: String, $limit: Int, $offset: Int, $after: String, $first: Int) {
    endpoints(applicationId: $applicationId, status: $status, search: $search, limit: $limit, offset: $offset, after: $after, first: $first) {
      nodes {
        id
        applicationId
        url
        type
        typeConfig
        deliveryMode
        status
        rateLimitRps
        timeoutMs
        authType
        ordered
        outputFormat
        healthScore
        disabledReason
        healthConfig {
          windowHours
          disableBelow
        }
        transformationId
        connectorType
        createdAt
        application {
          id
          name
          uid
        }
      }
      pageInfo {
        total
        limit
        offset
        endCursor
        hasNextPage
      }
    }
  }
`)

export const EndpointQuery = graphql(`
  query Endpoint($id: UUID!) {
    endpoint(id: $id) {
      id
      applicationId
      url
      type
      typeConfig
      signingSecret
      deliveryMode
      pollApiKeyPrefix
      pollApiKey
      filterConfig {
        eventTypes
        rules {
          path
          operator
          value
          rules {
            path
            operator
            value
            rules {
              path
              operator
              value
            }
          }
        }
      }
      transformationId
      transformation {
        id
        name
      }
      status
      rateLimitRps
      timeoutMs
      retryPolicy {
        maxAttempts
        initialDelay
        maxDelay
        backoffFactor
      }
      headers
      authType
      oauth2Config {
        tokenUrl
        clientId
        clientSecret
        scopes
        audience
      }
      mtlsCert
      mtlsKey
      ordered
      outputFormat
      healthScore
      disabledReason
      healthConfig {
        windowHours
        disableBelow
      }
      delay
      channels
      connectorType
      autoDeletePayload
      metrics(hours: 24) {
        p50
        p95
        p99
        max
      }
      blockedDelivery {
        id
        messageId
        status
        attempts
        maxAttempts
        createdAt
        message {
          id
          eventType
        }
      }
      createdAt
      application {
        id
        name
        uid
      }
    }
  }
`)

export const MessagesQuery = graphql(`
  query Messages($applicationId: UUID, $eventType: String, $status: MessageStatus, $payloadSearch: String, $search: String, $limit: Int, $offset: Int, $after: String, $first: Int) {
    messages(applicationId: $applicationId, eventType: $eventType, status: $status, payloadSearch: $payloadSearch, search: $search, limit: $limit, offset: $offset, after: $after, first: $first) {
      nodes {
        id
        applicationId
        eventType
        idempotencyKey
        status
        createdAt
        application {
          id
          name
          uid
        }
      }
      pageInfo {
        total
        limit
        offset
        endCursor
        hasNextPage
      }
    }
  }
`)

export const MessageQuery = graphql(`
  query Message($id: UUID!) {
    message(id: $id) {
      id
      applicationId
      eventType
      payload
      idempotencyKey
      status
      createdAt
      application {
        id
        name
        uid
      }
      outboundDeliveries {
        id
        endpointId
        status
        attempts
        maxAttempts
        nextAttemptAt
        createdAt
        endpoint {
          id
          url
        }
      }
    }
  }
`)

export const OutboundDeliveriesQuery = graphql(`
  query OutboundDeliveries($messageId: UUID, $endpointId: UUID, $applicationId: UUID, $status: DeliveryStatus, $search: String, $limit: Int, $offset: Int, $after: String, $first: Int) {
    outboundDeliveries(messageId: $messageId, endpointId: $endpointId, applicationId: $applicationId, status: $status, search: $search, limit: $limit, offset: $offset, after: $after, first: $first) {
      nodes {
        id
        messageId
        endpointId
        status
        attempts
        maxAttempts
        nextAttemptAt
        createdAt
        message {
          id
          eventType
        }
        endpoint {
          id
          url
        }
      }
      pageInfo {
        total
        limit
        offset
        endCursor
        hasNextPage
      }
    }
  }
`)

export const OutboundDeliveryQuery = graphql(`
  query OutboundDelivery($id: UUID!) {
    outboundDelivery(id: $id) {
      id
      messageId
      endpointId
      status
      attempts
      maxAttempts
      nextAttemptAt
      createdAt
      message {
        id
        eventType
        applicationId
        application {
          id
          name
        }
      }
      endpoint {
        id
        url
      }
      deliveryAttempts {
        id
        attemptNumber
        responseStatus
        responseBody
        error
        durationMs
        attemptedAt
      }
    }
  }
`)

export const CreateApplicationMutation = graphql(`
  mutation CreateApplication($input: CreateApplicationInput!) {
    createApplication(input: $input) {
      id
      name
      uid
      createdAt
    }
  }
`)

export const UpdateApplicationMutation = graphql(`
  mutation UpdateApplication($id: UUID!, $input: UpdateApplicationInput!) {
    updateApplication(id: $id, input: $input) {
      id
      name
      uid
      createdAt
    }
  }
`)

export const DeleteApplicationMutation = graphql(`
  mutation DeleteApplication($id: UUID!) {
    deleteApplication(id: $id)
  }
`)

export const CreateEndpointMutation = graphql(`
  mutation CreateEndpoint($input: CreateEndpointInput!) {
    createEndpoint(input: $input) {
      id
      applicationId
      url
      type
      typeConfig
      deliveryMode
      pollApiKey
      status
      rateLimitRps
      timeoutMs
      authType
      ordered
      outputFormat
      healthScore
      disabledReason
      healthConfig {
        windowHours
        disableBelow
      }
      transformationId
      transformation {
        id
        name
      }
      connectorType
      autoDeletePayload
      delay
      channels
      createdAt
    }
  }
`)

export const UpdateEndpointMutation = graphql(`
  mutation UpdateEndpoint($id: UUID!, $input: UpdateEndpointInput!) {
    updateEndpoint(id: $id, input: $input) {
      id
      applicationId
      url
      type
      typeConfig
      deliveryMode
      pollApiKey
      status
      rateLimitRps
      timeoutMs
      authType
      ordered
      outputFormat
      healthScore
      disabledReason
      healthConfig {
        windowHours
        disableBelow
      }
      transformationId
      transformation {
        id
        name
      }
      connectorType
      autoDeletePayload
      delay
      channels
      createdAt
    }
  }
`)

export const DeleteEndpointMutation = graphql(`
  mutation DeleteEndpoint($id: UUID!) {
    deleteEndpoint(id: $id)
  }
`)

export const RotateEndpointSecretMutation = graphql(`
  mutation RotateEndpointSecret($id: UUID!) {
    rotateEndpointSecret(id: $id) {
      id
      signingSecret
    }
  }
`)

export const SendMessageMutation = graphql(`
  mutation SendMessage($input: SendMessageInput!) {
    sendMessage(input: $input) {
      id
      applicationId
      eventType
      status
      createdAt
    }
  }
`)

export const MessageReceivedSubscription = graphql(`
  subscription MessageReceived($applicationId: UUID) {
    messageReceived(applicationId: $applicationId) {
      id
      applicationId
      eventType
      status
      createdAt
    }
  }
`)

export const OutboundDeliveryUpdatedSubscription = graphql(`
  subscription OutboundDeliveryUpdated($endpointId: UUID, $messageId: UUID) {
    outboundDeliveryUpdated(endpointId: $endpointId, messageId: $messageId) {
      id
      messageId
      endpointId
      status
      attempts
      maxAttempts
      createdAt
    }
  }
`)

export const OutboundDLQEntriesQuery = graphql(`
  query OutboundDLQEntries($messageId: UUID, $replayed: Boolean, $search: String, $limit: Int, $offset: Int, $after: String, $first: Int) {
    outboundDlqEntries(messageId: $messageId, replayed: $replayed, search: $search, limit: $limit, offset: $offset, after: $after, first: $first) {
      nodes {
        id
        deliveryId
        messageId
        lastError
        replayedAt
        createdAt
        message {
          id
          eventType
        }
        delivery {
          id
          endpointId
          endpoint {
            id
            url
          }
        }
      }
      pageInfo {
        total
        limit
        offset
        endCursor
        hasNextPage
      }
    }
  }
`)

export const OutboundDLQEntryQuery = graphql(`
  query OutboundDLQEntry($id: UUID!) {
    outboundDlqEntry(id: $id) {
      id
      deliveryId
      messageId
      lastError
      replayedAt
      createdAt
      message {
        id
        eventType
        applicationId
        application {
          id
          name
        }
      }
      delivery {
        id
        endpointId
        status
        attempts
        maxAttempts
        createdAt
        endpoint {
          id
          url
        }
        deliveryAttempts {
          id
          attemptNumber
          responseStatus
          responseBody
          error
          durationMs
          attemptedAt
        }
      }
    }
  }
`)

export const ReplayOutboundDLQEntryMutation = graphql(`
  mutation ReplayOutboundDLQEntry($id: UUID!) {
    replayOutboundDlqEntry(id: $id)
  }
`)

export const SkipOutboundDLQEntryMutation = graphql(`
  mutation SkipOutboundDLQEntry($id: UUID!) {
    skipOutboundDlqEntry(id: $id)
  }
`)

export const ReplayAllOutboundDLQMutation = graphql(`
  mutation ReplayAllOutboundDLQ {
    replayAllOutboundDlq {
      deliveries
    }
  }
`)

export const PurgeOutboundDLQMutation = graphql(`
  mutation PurgeOutboundDLQ($olderThan: String!) {
    purgeOutboundDlq(olderThan: $olderThan) {
      purged
    }
  }
`)

export const OutboundDLQEntryCreatedSubscription = graphql(`
  subscription OutboundDLQEntryCreated {
    outboundDlqEntryCreated {
      id
      deliveryId
      messageId
      lastError
      createdAt
    }
  }
`)

export const PollOutboundDeliveriesQuery = graphql(`
  query PollOutboundDeliveries($endpointId: UUID!, $cursor: String, $limit: Int) {
    pollOutboundDeliveries(endpointId: $endpointId, cursor: $cursor, limit: $limit) {
      nodes {
        id
        messageId
        endpointId
        status
        attempts
        maxAttempts
        createdAt
        message {
          id
          eventType
        }
        endpoint {
          id
          url
        }
      }
      pageInfo {
        total
        limit
        offset
        endCursor
        hasNextPage
      }
    }
  }
`)

export const AckOutboundDeliveriesMutation = graphql(`
  mutation AckOutboundDeliveries($endpointId: UUID!, $deliveryIds: [UUID!]!) {
    ackOutboundDeliveries(endpointId: $endpointId, deliveryIds: $deliveryIds)
  }
`)

export const RegenerateOutboundPollApiKeyMutation = graphql(`
  mutation RegenerateOutboundPollApiKey($endpointId: UUID!) {
    regenerateOutboundPollApiKey(endpointId: $endpointId) {
      id
      deliveryMode
      pollApiKeyPrefix
      pollApiKey
    }
  }
`)

export const BroadcastMessageMutation = graphql(`
  mutation BroadcastMessage($input: BroadcastMessageInput!) {
    broadcastMessage(input: $input) {
      id
      applicationId
      eventType
      status
      createdAt
    }
  }
`)

export const SendDynamicMessageMutation = graphql(`
  mutation SendDynamicMessage($input: SendDynamicMessageInput!) {
    sendDynamicMessage(input: $input) {
      id
      messageId
      endpointId
      status
      attempts
      maxAttempts
      createdAt
    }
  }
`)

export const GeneratePortalTokenMutation = graphql(`
  mutation GeneratePortalToken($applicationId: UUID!) {
    generatePortalToken(applicationId: $applicationId) {
      token
      expiresAt
    }
  }
`)

export const ForceRetryOutboundDeliveryMutation = graphql(`
  mutation ForceRetryOutboundDelivery($id: UUID!) {
    forceRetryOutboundDelivery(id: $id) {
      id
      status
      attempts
    }
  }
`)

export const BulkRetryOutboundDeliveriesMutation = graphql(`
  mutation BulkRetryOutboundDeliveries($filter: BulkRetryOutboundFilterInput!) {
    bulkRetryOutboundDeliveries(filter: $filter) {
      count
    }
  }
`)

export const BulkDeleteApplicationsMutation = graphql(`
  mutation BulkDeleteApplications($ids: [UUID!]!) {
    bulkDeleteApplications(ids: $ids)
  }
`)

export const BulkDeleteEndpointsMutation = graphql(`
  mutation BulkDeleteEndpoints($ids: [UUID!]!) {
    bulkDeleteEndpoints(ids: $ids)
  }
`)

export const BulkDeleteMessagesMutation = graphql(`
  mutation BulkDeleteMessages($ids: [UUID!]!) {
    bulkDeleteMessages(ids: $ids)
  }
`)
